import React, { useState, useEffect } from "react";
import Pagination from "@mui/material/Pagination";
import { ColorRing } from "react-loader-spinner";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

import API_BASE_URL from "../../../../Constants";
import ListHeader from "../../../../components/ListHeader/NoCardView/index";
import RowsPerPageSelector from "../../../../components/Tables/Pagination/RowsPerPageSelector";
import ViewInspectionDeviceListModal from "./ViewModal";
import InspectionWizardModal from "../../../setup/components/Building/Actions/Run the Inspection Wizard/InspectionWizardModalNoDropdown";

const inspectionFields = [
  { key: "actions", label: "Actions" },
  { key: "inspectionNumber", label: "Inspection #" },
  { key: "buildingReference", label: "Building" },
  { key: "managementCompanyName", label: "Mgmt Co" },
  { key: "agencyName", label: "Agency" },
  { key: "test", label: "Test Label" },
  { key: "approvalDate", label: "Approval Date" },
  { key: "lastDate", label: "Last Date" },
  { key: "nextDate", label: "Next Date" },
  { key: "status", label: "Status" },
];

// Example filters
const inspectionFilters = [
  "All Inspections",
  "Scheduled Today",
  "Scheduled Next Day",
  "Scheduled This Week",
  "Scheduled Next Week",
  "Scheduled This Month",
  "Compliance 1",
  "Compliance 2",
  "Compliance 3",
];

const taskTypeOptions = ["All Tasks", "Perform", "Witness", "Both"];

// Simple local filter example
function filterInspectionsBySelection(array, filter) {
  if (filter === "All Inspections") return array;
  // Real filtering logic goes here if needed.
  return array;
}

export default function InspectionDevicesList() {
  const [inspections, setInspections] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");

  // Example filters
  const [selectedInspectionFilter, setSelectedInspectionFilter] =
    useState("All Inspections");
  const [selectedTaskType, setSelectedTaskType] = useState("All Tasks");

  const [isLoading, setIsLoading] = useState(false);

  // “View” modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedInspection, setSelectedInspection] = useState(null);

  // “Add Inspection” wizard
  const [isWizardOpen, setIsWizardOpen] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // --------------------------------------
  // 1) Fetch from server on mount or changes
  // --------------------------------------
  useEffect(() => {
    fetchInspections();
    // eslint-disable-next-line
  }, [currentPage, rowsPerPage]);

  async function fetchInspections() {
    setIsLoading(true);
    try {
      const pageIndex = currentPage - 1;
      const url = `${API_BASE_URL}/api/inspections/with-joins-listdto?page=${pageIndex}&size=${rowsPerPage}`;
      const resp = await fetch(url, { credentials: "include" });
      if (!resp.ok) {
        throw new Error(`HTTP error => ${resp.status}`);
      }
      const data = await resp.json();
      setInspections(data.content || []);
      setTotalPages(data.totalPages || 1);
    } catch (err) {
      console.error("Error fetching inspections =>", err);
      setInspections([]);
      setTotalPages(1);
    } finally {
      setIsLoading(false);
    }
  }

  // If you need to refresh the list after certain actions
  const refreshList = async () => {
    setCurrentPage(1);
    await fetchInspections();
  };

  // --------------------------------------
  // 2) Searching
  // --------------------------------------
  const handleSearchChange = (e) => setSearchQuery(e.target.value);

  const handleSearch = () => {
    setCurrentPage(1);
    fetchInspections();
  };

  // --------------------------------------
  // 3) Filters (local or server)
  // --------------------------------------
  let displayedInspections = inspections;
  displayedInspections = filterInspectionsBySelection(
    displayedInspections,
    selectedInspectionFilter
  );
  // Additional logic if you filter by selectedTaskType, etc.

  // --------------------------------------
  // 4) Excel Export
  // --------------------------------------
  const handleExcelExport = () => {
    if (inspections.length === 0) {
      alert("No data to export!");
      return;
    }
    const worksheet = XLSX.utils.json_to_sheet(inspections);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
    const wbout = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([wbout], { type: "application/octet-stream" }),
      "Inspections.xlsx"
    );
  };

  // --------------------------------------
  // 5) “View” => open modal
  // --------------------------------------
  const handleViewClick = (insp) => {
    setSelectedInspection(insp);
    setIsModalOpen(true);
  };
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedInspection(null);
  };

  // --------------------------------------
  // 6) “Add” => wizard
  // --------------------------------------
  const handleOpenCreateModal = () => setIsWizardOpen(true);
  const handleCloseWizard = () => setIsWizardOpen(false);

  // --------------------------------------
  // 7) Pagination
  // --------------------------------------
  const handlePageChange = (event, page) => setCurrentPage(page);

  function handleRowsPerPageChange(newValue) {
    setRowsPerPage(newValue);
    setCurrentPage(1);
    fetchInspections();
  }

  // --------------------------------------
  // RENDER
  // --------------------------------------
  return (
    <div className="w-full h-screen flex flex-col">
      {/* Header / Search / Filters / Add */}
      <ListHeader
        title="Inspection Devices List"
        searchQuery={searchQuery}
        handleSearchChange={handleSearchChange}
        handleSearch={handleSearch}
        handleOpenCreateModal={handleOpenCreateModal}
      >
        {/* Filter dropdowns */}
        <select
          value={selectedInspectionFilter}
          onChange={(e) => setSelectedInspectionFilter(e.target.value)}
          type="button"
          className="px-4 py-2 border-2 border-sss-blue-sel rounded-lg
                     text-sss-blue-sel font-bold hover:bg-sss-blue-sel
                     hover:text-white transition flex items-center"
        >
          {inspectionFilters.map((f) => (
            <option key={f} value={f}>
              {f}
            </option>
          ))}
        </select>

        <select
          value={selectedTaskType}
          onChange={(e) => setSelectedTaskType(e.target.value)}
          type="button"
          className="px-4 py-2 border-2 border-sss-blue-sel rounded-lg
                     text-sss-blue-sel font-bold hover:bg-sss-blue-sel
                     hover:text-white transition flex items-center"
        >
          {taskTypeOptions.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>

        {/* Excel button */}
        <button
          onClick={handleExcelExport}
          type="button"
          className="px-4 py-2 border-2 border-sss-blue-sel rounded-lg
                     text-sss-blue-sel font-bold hover:bg-sss-blue-sel
                     hover:text-white transition flex items-center"
        >
          Excel
        </button>
      </ListHeader>

      {/* Main scroll container for the table */}
      <div className="flex-1 overflow-x-auto mt-2">
        {isLoading && (
          <div className="flex justify-center items-center py-8">
            <ColorRing
              visible
              height="200"
              width="200"
              ariaLabel="color-ring-loading"
              colors={["#033B5C", "#033B5C", "#033B5C", "#033B5C", "#033B5C"]}
            />
          </div>
        )}

        {!isLoading && (
          <table className="min-w-full bg-white rounded-lg shadow-lg border-2 border-gray-200">
            <thead className="bg-gray-200">
              <tr>
                {inspectionFields.map((field) => (
                  <th
                    key={field.key}
                    className="px-4 py-2 text-left font-bold text-sss-blue-sel"
                  >
                    {field.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="text-gray-700">
              {displayedInspections.length > 0 ? (
                displayedInspections.map((insp, idx) => (
                  <tr
                    key={idx}
                    className="border-t hover:bg-gray-50 transition"
                  >
                    {inspectionFields.map((col) => {
                      if (col.key === "actions") {
                        return (
                          <td
                            key={col.key}
                            className="px-4 py-2 whitespace-nowrap"
                          >
                            <button
                              className="text-sss-info font-semibold hover:underline"
                              onClick={() => handleViewClick(insp)}
                            >
                              View
                            </button>
                          </td>
                        );
                      }
                      return (
                        <td
                          key={col.key}
                          className="px-4 py-2 whitespace-nowrap"
                        >
                          {insp[col.key] ?? "N/A"}
                        </td>
                      );
                    })}
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={inspectionFields.length}
                    className="text-center py-4 text-sm text-red-600"
                  >
                    No inspections found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Bottom bar pinned at the bottom */}
      <div className="fixed bottom-0 left-0 w-full bg-white border-t border-gray-300 p-4 flex justify-between items-center">
        <Pagination
          count={totalPages}
          page={currentPage}
          onChange={handlePageChange}
          color="primary"
          shape="rounded"
        />

        <RowsPerPageSelector
          rowsPerPage={rowsPerPage}
          setRowsPerPage={handleRowsPerPageChange}
        />
      </div>

      {/* “View” Modal */}
      {isModalOpen && selectedInspection && (
        <ViewInspectionDeviceListModal
          open={isModalOpen}
          inspectionNumber={selectedInspection.inspectionNumber}
          selectedInspection={selectedInspection}
          onClose={handleCloseModal}
          refreshList={refreshList}
        />
      )}

      {/* “Add” Wizard */}
      {isWizardOpen && (
        <InspectionWizardModal
          onClose={handleCloseWizard}
          refreshList={refreshList}
        />
      )}
    </div>
  );
}
